import React from "react";
import PropTypes from "prop-types";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ErrorBoundary from "../components/ErrorBoundary/ErrorBoundary";
import AuthenticatedLayout from "../components/AuthenticatedLayout/AuthenticatedLayout";
import ProtectedRoute from "../components/ProtectedRoute/ProtectedRoute";
import { PERMISSIONS } from "../auth/permissions";
import LoginPage from "../pages/LoginPage/LoginPage";
import AccessDeniedPage from "../pages/AccessDeniedPage/AccessDeniedPage";
import JobsDashboardPage from "../pages/JobsDashboardPage/JobsDashboardPage";
import ServicesPage from "../pages/ServicesPage/ServicesPage";
import UploadFilesPage from "../pages/UploadFilesPage/UploadFilesPage";
import SyncPage from "../pages/SyncPage/SyncPage";
import DownloadPage from "../pages/DownloadPage/DownloadPage";
import VoicePreferencesPage from "../pages/VoicePreferencesPage/VoicePreferencesPage";
import NotFoundPage from "../pages/NotFoundPage/NotFoundPage";

function RouteErrorBoundary({ children }) {
  return <ErrorBoundary onReset={() => (window.location.href = "/")}>{children}</ErrorBoundary>;
}

// Renamed from ProtectedRoute → AuthLayout to avoid collision with the
// permission-based <ProtectedRoute> imported from components/ProtectedRoute.
function AuthLayout({ children }) {
  return (
    <RouteErrorBoundary>
      <AuthenticatedLayout>{children}</AuthenticatedLayout>
    </RouteErrorBoundary>
  );
}

function AppRouter() {
  return (
    <BrowserRouter>
      <ErrorBoundary onReset={() => (window.location.href = "/")}>
        <Routes>
          {/* ── Login (no layout) ── */}
          <Route
            path="/login"
            element={
              <RouteErrorBoundary>
                <LoginPage />
              </RouteErrorBoundary>
            }
          />

          {/* ── Access Denied (no AuthenticatedLayout — avoids redirect loop) ── */}
          <Route
            path="/access-denied"
            element={
              <RouteErrorBoundary>
                <AccessDeniedPage />
              </RouteErrorBoundary>
            }
          />

          {/* ── Video Sync Dashboard ── */}
          <Route
            path="/dashboard"
            element={
              <AuthLayout>
                <ProtectedRoute permission={PERMISSIONS.VIEW_JOB_STATUS}>
                  <JobsDashboardPage />
                </ProtectedRoute>
              </AuthLayout>
            }
          />

          {/* ── Select Service ── */}
          <Route
            path="/services"
            element={
              <AuthLayout>
                <ProtectedRoute permission={PERMISSIONS.UPLOAD_FILE}>
                  <ServicesPage />
                </ProtectedRoute>
              </AuthLayout>
            }
          />

          {/* ── Upload Files ── */}
          <Route
            path="/upload"
            element={
              <AuthLayout>
                <ProtectedRoute permission={PERMISSIONS.UPLOAD_FILE}>
                  <UploadFilesPage />
                </ProtectedRoute>
              </AuthLayout>
            }
          />

          {/* ── Sync / Processing ── */}
          <Route
            path="/sync"
            element={
              <AuthLayout>
                <ProtectedRoute permission={PERMISSIONS.RUN_SYNC}>
                  <SyncPage />
                </ProtectedRoute>
              </AuthLayout>
            }
          />

          {/* ── Download ── */}
          <Route
            path="/download"
            element={
              <AuthLayout>
                <ProtectedRoute permission={PERMISSIONS.DOWNLOAD_FILE}>
                  <DownloadPage />
                </ProtectedRoute>
              </AuthLayout>
            }
          />

          {/* ── Voice Preferences — Admin only ── */}
          <Route
            path="/voice-preferences"
            element={
              <AuthLayout>
                <ProtectedRoute permission={PERMISSIONS.MANAGE_VOICE_PREFERENCES}>
                  <VoicePreferencesPage />
                </ProtectedRoute>
              </AuthLayout>
            }
          />

          {/* ── Root → redirect to login ── */}
          <Route path="/" element={<Navigate to="/login" replace />} />

          {/* ── 404 catch-all ── */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

RouteErrorBoundary.propTypes = {
  children: PropTypes.node,
};

AuthLayout.propTypes = {
  children: PropTypes.node,
};

export default AppRouter;
